import { Alert } from "@mui/material";
import * as React from "react";

import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

interface ErrorBoundaryProps extends React.PropsWithChildren {
  fallback?: (props: { error: Error }) => React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | undefined;
}

/**
 * Sentry error reporter that uses our standard ErrorMonitoringService reporting logic.
 *
 * It's based on Sentry's guide on manually capturing errors here:
 * https://docs.sentry.io/platforms/javascript/guides/react/features/error-boundary/#manually-capturing-errors
 *
 * Yes, as of May 2025, Sentry's docs recommend using a class component for catching errors.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      error: undefined,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      error,
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    ErrorMonitoringService.captureError(error, info as Record<string, unknown>);
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    } else if (!this.props.fallback) {
      return (
        <Alert severity="error">
          Something went wrong. Please contact{" "}
          <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
          problem persists.
        </Alert>
      );
    } else {
      const fallback = this.props.fallback;
      return fallback({ error: this.state.error });
    }
  }
}
